import React from 'react';
import {toast} from "react-toastify";
import {FaCheck, FaXmark} from "react-icons/fa6";
import {useActionFeedback} from '../../hooks/use-action-feedback.js';
import {attachToastFeedback, getActionFeedbackType} from '../../utils/action-feedback.js';

attachToastFeedback(toast);

const PERSISTED_SAVE_ACTION_KEYS = new Set([
    "salvar",
    "salvar e adicionar novo",
    "salvar e copiar",
]);

const Button = ({children, target = "primary", type, role, reference, id, className, onClick, onKeyDown, onMouseDown, disabled, title, style, loading = false, loadingText, loadingClassName, status = "idle", successText, successIcon, successClassName, actionFeedbackType = "none", actionFeedbackPrimary, onActionFeedbackSuccess, deferActionFeedback = false, unstyled = false}) => {
    const defaultClass = "font-bold flex justify-center cursor-pointer h-[32px] items-center gap-1 focus:ring-1 focus:outline-none rounded-md shadow text-[13px] px-4 py-2 text-center disabled:bg-[#9B9B9B] disabled:pointer-events-none disabled:opacity-70"

    let defaultButton = "text-gray-700 bg-white hover:bg-gray-200 focus:ring-0 border border-gray-500 disabled:hover::bg-white";
    const buttonClass = {
        primary: "text-white bg-[#063A6C] hover:bg-blue-800 focus:ring-blue-300 disabled:hover:bg-blue-700",
        secondary: "text-white bg-gray-600 hover:bg-gray-500 focus:ring-gray-500 disabled:hover:bg-gray-400",
        success: "text-white bg-green-600 hover:bg-green-700 focus:ring-green-300 disabled:hover:bg-green-600",
        warning: "text-white bg-[#fbae1d] hover:bg-[#ffb831] focus:ring-orange-300 disabled:hover:bg-orange-500",
        danger: "text-white bg-red-700 hover:bg-red-600 focus:ring-red-300 disabled:hover:bg-[#ed5565]",
        attach: "text-[#5DACF6] bg-white border border-[#5DACF6] hover:bg-blue-100 focus:ring-[#f3f4f6] disabled:bg-[#f3f4f6]",
        attached: "text-white bg-[#5DACF6] border border-[#5DACF6] hover:bg-blue-300 focus:ring-[#f3f4f6] disabled:bg-[#f3f4f6]",
        "primary-outline": "text-gray-700 bg-white hover:bg-gray-200 focus:ring-0 border border-gray-500 disabled:bg-gray-200",
        info: "text-white border border-[#23c6c8] bg-[#39c0ed] hover:bg-[#4ecaf3]",
    }

    const getChildrenText = (value) => React.Children.toArray(value).map(child => {
        if (typeof child === "string" || typeof child === "number") return String(child);
        if (React.isValidElement(child)) return getChildrenText(child.props.children);
        return "";
    }).join(" ");

    const feedbackType = getActionFeedbackType(actionFeedbackType);
    const normalizedChildrenText = getChildrenText(children).replace(/\s+/g, " ").trim().toLowerCase();
    const normalizedActionKey = (normalizedChildrenText || String(title ?? "").trim().toLowerCase()).replace(/\s+/g, " ");
    const receivePersistedSuccess = feedbackType === "save" && (actionFeedbackPrimary ?? PERSISTED_SAVE_ACTION_KEYS.has(normalizedActionKey));
    const feedback = useActionFeedback({
        type: feedbackType,
        actionKey: normalizedActionKey,
        externalLoading: loading,
        onSuccess: onActionFeedbackSuccess,
        receivePersistedSuccess,
    });

    const feedbackStatus = status === "idle" ? feedback.status : status;
    const isSuccess = feedbackStatus === "success";
    const isDeleteSuccess = feedbackType === "delete" && isSuccess;
    const isLoading = loading || feedbackStatus === "loading" || isDeleteSuccess;

    const resolvedTarget = feedbackType === "delete" ? "danger" : target;
    const resolvedLoadingText = loadingText ?? (feedbackType === "delete" ? "Excluindo..." : "Salvando...");
    const resolvedSuccessText = successText ?? feedback.successMessage ?? (feedbackType === "delete" ? "Excluído com sucesso" : "Salvo com sucesso");
    const resolvedSuccessIcon = successIcon ?? (feedbackType === "delete" ? <FaXmark size={17}/> : <FaCheck/>);
    const resolvedSuccessClassName = successClassName ?? (feedbackType ? "disabled:bg-green-700 disabled:!text-white disabled:opacity-100!" : "");

    const appliedLoadingClassName = isLoading ? ["!bg-[#9B9B9B] text-white!", loadingClassName].filter(Boolean).join(" ") : "";
    const appliedSuccessClassName = isSuccess && feedbackType !== "delete" ? resolvedSuccessClassName : "";
    const buttonContent = isSuccess ? <>{resolvedSuccessIcon} {resolvedSuccessText}</> : children;

    const handleClick = (event) => {
        if (feedbackType === "delete" || deferActionFeedback) {
            if (feedback.isPrepared) return;
            feedback.prepare();
        } else if (feedbackType) {
            feedback.start();
        }

        try {
            const result = onClick?.(event);
            if (feedbackType && result && typeof result.then === "function") {
                return Promise.resolve(result).catch((error) => {
                    feedback.reset();
                    throw error;
                });
            }
            return result;
        } catch (error) {
            if (feedbackType) feedback.reset();
            throw error;
        }
    };

    return (
        <button
            ref={reference}
            id={id}
            role={role}
            className={[(unstyled ? "" : defaultClass), (unstyled ? "" : (buttonClass[resolvedTarget] ?? defaultButton)), (className ?? ""), appliedSuccessClassName, appliedLoadingClassName].filter(Boolean).join(" ")}
            data-action-loading={isLoading ? "true" : undefined}
            onClick={handleClick}
            onKeyDown={onKeyDown}
            onMouseDown={onMouseDown}
            disabled={disabled || isLoading || isSuccess}
            title={title}
            style={style}
            type={type}
        >
            {
                isLoading ?
                    <>
                        <span className="inline-block size-5 rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] animate-spin text-gray-300"/> {resolvedLoadingText}
                    </>
                : buttonContent
            }
        </button>
    )
};

export default Button;